/**
 * Dashboard Business Logic Helpers
 * Phase 3: Dashboard Implementation
 *
 * All dashboard business logic is isolated in pure functions for testability.
 */

import type { AuditSummaryStats, RecentAudit } from './api'

/**
 * Risk level classification
 */
export type RiskLevel = 'critical' | 'high' | 'low'

/**
 * Attention item with computed risk metadata
 */
export interface AttentionItem extends RecentAudit {
  riskScore: number
  riskLevel: RiskLevel
  title: string
  description: string
  actionLink: string
  actionLabel: string
}

/**
 * Risk posture summary
 */
export interface RiskPosture {
  level: RiskLevel
  score: number
  criticalIssues: number
  highIssues: number
  description: string
}

/**
 * Recommended action
 */
export interface RecommendedAction {
  title: string
  description: string
  priority: 'high' | 'medium' | 'low'
  actionLink: string
  actionLabel: string
  reasoning: string
}

/**
 * Severity distribution
 */
export interface SeverityDistribution {
  critical: number
  high: number
  medium: number
  low: number
  info: number
  total: number
}

/**
 * Calculate weighted risk score for an audit.
 * Formula: (critical × 10) + (high × 5) + (medium × 2) + (low × 1)
 * Capped at 100 so the UI "/100" scale stays honest.
 */
export function calculateAuditRiskScore(audit: RecentAudit): number {
  const raw =
    audit.critical_count * 10 +
    audit.high_count * 5 +
    audit.medium_count * 2 +
    audit.low_count * 1
  return Math.min(100, raw)
}

/**
 * Keep the newest audit per project (by created_at, then id).
 * Prevents re-runs of the same project from appearing as separate risks.
 */
export function latestAuditPerProject(audits: RecentAudit[]): RecentAudit[] {
  const best = new Map<string, RecentAudit>()
  for (const audit of audits) {
    const prev = best.get(audit.project_id)
    if (!prev) {
      best.set(audit.project_id, audit)
      continue
    }
    const prevTs = new Date(prev.created_at).getTime()
    const nextTs = new Date(audit.created_at).getTime()
    if (nextTs > prevTs || (nextTs === prevTs && audit.id > prev.id)) {
      best.set(audit.project_id, audit)
    }
  }
  return Array.from(best.values())
}

/**
 * Determine risk level based on critical and high finding counts.
 */
export function determineRiskLevel(criticalCount: number, highCount: number): RiskLevel {
  if (criticalCount > 0) return 'critical'
  if (highCount > 0) return 'high'
  return 'low'
}

/**
 * Filter and order audits requiring attention.
 * One entry per project (latest run only). Includes critical/high or failed/partial.
 * Sorted by risk score descending, ties broken by created_at descending.
 */
export function getAttentionItems(audits: RecentAudit[]): AttentionItem[] {
  return latestAuditPerProject(audits)
    .filter(audit => 
      audit.critical_count > 0 ||
      audit.high_count > 0 ||
      audit.status === 'failed' ||
      audit.status === 'partial'
    )
    .map(audit => {
      const riskScore = calculateAuditRiskScore(audit)
      const riskLevel = determineRiskLevel(audit.critical_count, audit.high_count)
      
      // Generate title and description
      let title: string
      let description: string
      if (audit.status === 'failed') {
        title = `${audit.project_name} Audit Failed`
        description = 'Review failed audit run for errors'
      } else if (riskLevel === 'critical') {
        title = `${audit.critical_count} Critical Finding${audit.critical_count > 1 ? 's' : ''} in ${audit.project_name}`
        description = 'Requires immediate security review and remediation'
      } else if (riskLevel === 'high') {
        title = `${audit.high_count} High-Priority Finding${audit.high_count > 1 ? 's' : ''} in ${audit.project_name}`
        description = 'Significant security concerns requiring attention'
      } else {
        title = `Review ${audit.project_name} Audit`
        description = `${audit.total_findings} finding${audit.total_findings > 1 ? 's' : ''} detected`
      }
      
      return {
        ...audit,
        riskScore,
        riskLevel,
        title,
        description,
        actionLink: `/audit/${audit.id}`,
        actionLabel: 'Review Audit',
      }
    })
    .sort((a, b) => {
      // Sort by risk score descending
      if (b.riskScore !== a.riskScore) {
        return b.riskScore - a.riskScore
      }
      // Tie-breaker: most recent first
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    })
}

/**
 * Calculate overall risk posture from audit summary stats.
 */
export function calculateRiskPosture(stats: AuditSummaryStats): RiskPosture {
  const level = determineRiskLevel(stats.critical_findings, stats.high_findings)
  
  // Weighted score on a 0–100 scale (same weights as per-audit score).
  const raw =
    stats.critical_findings * 10 +
    stats.high_findings * 5 +
    stats.medium_findings * 2 +
    stats.low_findings * 1
  const score = Math.min(100, raw)

  let description: string
  if (level === 'critical') {
    description = `Critical security concerns detected (${stats.critical_findings} critical findings)`
  } else if (level === 'high') {
    description = `High-priority issues requiring attention (${stats.high_findings} high findings)`
  } else {
    description = 'Low risk - no critical or high-priority issues detected'
  }

  return {
    level,
    score,
    criticalIssues: stats.critical_findings,
    highIssues: stats.high_findings,
    description,
  }
}

/**
 * Get recommended next action based on audit state.
 * Priority-ordered decision tree (first match wins).
 */
export function getRecommendedAction(
  stats: AuditSummaryStats,
  _recentAudits: RecentAudit[]
): RecommendedAction | null {
  // 1. Critical findings exist
  if (stats.critical_findings > 0) {
    return {
      title: 'Investigate Critical Findings',
      description: `${stats.critical_findings} critical security issues require immediate attention`,
      priority: 'high',
      actionLink: '/findings?severity=critical',
      actionLabel: 'View Critical Findings',
      reasoning: 'Critical findings pose immediate security risks',
    }
  }

  // 2. Failed audits exist
  if (stats.failed_audits > 0) {
    return {
      title: 'Review Failed Audits',
      description: `${stats.failed_audits} audit${stats.failed_audits > 1 ? 's' : ''} failed to complete`,
      priority: 'high',
      actionLink: '/audits',
      actionLabel: 'View Audits',
      reasoning: 'Failed audits may indicate infrastructure or configuration issues',
    }
  }

  // 3. High findings exist
  if (stats.high_findings > 0) {
    return {
      title: 'Review High-Severity Findings',
      description: `${stats.high_findings} high-severity issues found`,
      priority: 'medium',
      actionLink: '/findings?severity=high',
      actionLabel: 'View High Findings',
      reasoning: 'High-severity findings should be addressed promptly',
    }
  }

  // 4. Audits running
  if (stats.running_audits > 0) {
    return {
      title: 'Monitor Running Audits',
      description: `${stats.running_audits} audit${stats.running_audits > 1 ? 's are' : ' is'} currently in progress`,
      priority: 'low',
      actionLink: '/audits',
      actionLabel: 'Monitor Audits',
      reasoning: 'Active audits are in progress',
    }
  }

  // 5. No audits yet (first-use state)
  if (stats.total_audits === 0) {
    return {
      title: 'Start Your First Audit',
      description: 'Begin by creating a project and running your first security audit',
      priority: 'medium',
      actionLink: '/projects',
      actionLabel: 'Create Project',
      reasoning: 'No audits have been run yet',
    }
  }

  // 6. Medium findings exist
  if (stats.medium_findings > 0) {
    return {
      title: 'Review Medium-Severity Findings',
      description: `${stats.medium_findings} medium-severity findings detected`,
      priority: 'low',
      actionLink: '/findings?severity=medium',
      actionLabel: 'View Medium Findings',
      reasoning: 'Medium-severity findings warrant review',
    }
  }

  // 7. Any findings exist
  if (stats.total_findings > 0) {
    return {
      title: 'Review All Findings',
      description: `${stats.total_findings} finding${stats.total_findings > 1 ? 's' : ''} across all severity levels`,
      priority: 'low',
      actionLink: '/findings',
      actionLabel: 'View All Findings',
      reasoning: 'Findings exist for review',
    }
  }

  // 8. All clear - no action needed
  return null
}

/**
 * Extract severity distribution from audit summary stats.
 */
export function getSeverityDistribution(stats: AuditSummaryStats): SeverityDistribution {
  return {
    critical: stats.critical_findings,
    high: stats.high_findings,
    medium: stats.medium_findings,
    low: stats.low_findings,
    info: stats.info_findings,
    total: stats.total_findings,
  }
}

/**
 * Check if this is a first-use state (no audits yet).
 */
export function isFirstUseState(stats: AuditSummaryStats): boolean {
  return stats.total_audits === 0
}

/**
 * Format relative time from ISO timestamp.
 * Returns human-readable format: "2 minutes ago", "3 hours ago", etc.
 */
export function formatRelativeTime(isoTimestamp: string, locale: 'ru' | 'en' = 'en'): string {
  const now = Date.now()
  const then = new Date(isoTimestamp).getTime()
  const diffMs = now - then

  if (diffMs < 0) {
    return locale === 'ru' ? 'только что' : 'just now'
  }

  const seconds = Math.floor(diffMs / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (seconds < 60) {
    return locale === 'ru' ? 'только что' : 'just now'
  } else if (minutes < 60) {
    if (locale === 'ru') return `${minutes} мин. назад`
    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
  } else if (hours < 24) {
    if (locale === 'ru') return `${hours} ч. назад`
    return `${hours} hour${hours === 1 ? '' : 's'} ago`
  } else if (days < 7) {
    if (locale === 'ru') return `${days} дн. назад`
    return `${days} day${days === 1 ? '' : 's'} ago`
  } else {
    return new Date(isoTimestamp).toLocaleDateString(locale === 'ru' ? 'ru-RU' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }
}
